import { useQuery, useMutation } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import useDialog from 'hooks/Shared/useDialog'
import { selectErrorDialog } from 'lib/helpers/errors.helpers'

import {
  createEmisor,
  deleteEmisor,
  getEmisorById,
  getEmisoresList,
  getEmisorFiles,
  getZipcodes,
  updateEmisor,
} from 'services/emisores'
import {
  setRazonesSocialesList,
  setSelectedRazonSocial,
} from 'actions/razonesSociales'

import Help from '@material-ui/icons/Help'
import ErrorI from '@material-ui/icons/ErrorOutline'

import RazonSocial, { RazonSocialForm } from 'models/razonSocial'
import { File, ZipCode, QueryOptions, MutationOptions } from 'models/general'
import { TModalIcon } from 'components/Shared/SydDialog/SydDialog'
import useLoadingAdditionalInformation from 'hooks/Shared/useLoadingAdditionalInformation'
import { IApiError } from 'services/Shared/shared.models'

export const useZipcodes = (
  zipcode: string,
  options: QueryOptions<ZipCode[]> = {}
) => {
  const dialog = useDialog()

  const { onSuccess, onError, noDialog = false, ...rest } = options

  const query = useQuery<ZipCode[], any>(
    ['get-zipcodes', zipcode],
    () => getZipcodes(zipcode),
    {
      ...rest,
      enabled: zipcode.length === 5,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        onSuccess && onSuccess(data)
      },
      onError: async error => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo obtener la lista de códigos postales.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Cerrar',
          })
        }

        onError && onError(error)
      },
    }
  )

  return query
}

const useEmisoresList = (
  workspaceId: number,
  options: QueryOptions<RazonSocial[]> = {}
) => {
  const dispatch = useDispatch()
  const dialog = useDialog()

  const {
    onBefore,
    onSuccess,
    onError,
    noSave = false,
    noDialog = false,
    ...rest
  } = options

  const query = useQuery<RazonSocial[], any>(
    ['get-emisores-list', workspaceId],
    () => {
      onBefore && onBefore()
      return getEmisoresList(workspaceId)
    },
    {
      ...rest,
      refetchOnWindowFocus: false,
      onSuccess: data => {
        !noSave && dispatch(setRazonesSocialesList(data))
        onSuccess && onSuccess(data)
      },
      onError: async error => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo obtener la lista de Emisores.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            query.refetch()
            return
          }
        }

        onError && onError(error)
      },
    }
  )

  return query
}

export const useEmisor = (
  workspaceId: number,
  emisorId: number,
  options: QueryOptions<RazonSocial> = {}
) => {
  const dispatch = useDispatch()
  const dialog = useDialog()

  const {
    onBefore,
    onSuccess,
    onError,
    noSave = false,
    noDialog = false,
    refreshOn = [],
    ...rest
  } = options

  const query = useQuery<RazonSocial, any>(
    ['get-emisor', emisorId, workspaceId, ...refreshOn],
    () => {
      onBefore && onBefore()
      return getEmisorById(workspaceId, emisorId)
    },
    {
      ...rest,
      onSuccess: data => {
        !noSave && dispatch(setSelectedRazonSocial(data))
        onSuccess && onSuccess(data)
      },
      onError: async error => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo obtener la información del emisor.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            query.refetch()
            return
          }
        }

        onError && onError(error)
      },
    }
  )

  return query
}

export const useEmisorFiles = (
  workspaceId: number,
  emisor?: RazonSocial,
  options: QueryOptions<File[]> = {}
) => {
  const dialog = useDialog()

  const {
    onBefore,
    onError,
    noDialog = false,
    refreshOn = [],
    ...rest
  } = options

  const query = useQuery<File[]>(
    ['get-emisor-files', workspaceId, ...refreshOn],
    () => {
      if (!emisor) throw new Error('Emisor cargado incorrectamente')
      onBefore && onBefore()
      return getEmisorFiles(workspaceId, emisor.idRfc)
    },
    {
      ...rest,
      onError: async (error: any) => {
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }

        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo obtener la lista de Emisores.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            query.refetch()
            return
          }
        }

        onError && onError(error)
      },
    }
  )

  return query
}

export const useAddEmisor = (
  workspaceId: number,
  options: MutationOptions<void, RazonSocialForm> = {}
) => {
  const dialog = useDialog()
  const { onBefore, onError, onMutate, noCheck, noDialog, ...rest } = options

  const mutation = useMutation<void, any, RazonSocialForm>(
    form => {
      onBefore && onBefore(form)
      return createEmisor(workspaceId, form)
    },
    {
      ...rest,
      onError: async (error, form, ctx) => {
        if (error.message === 'mutation-cancelled') return
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }
        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle: 'No se pudo agregar a este emisor.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            mutation.mutate(form)
            return
          }
        }

        onError && onError(error, form, ctx)
      },
    }
  )

  return mutation
}

export const useUpdateEmisor = (
  workspaceId: number,
  emisor?: RazonSocial,
  options: MutationOptions<void, RazonSocialForm> = {}
) => {
  const dialog = useDialog()
  const { onBefore, onError, onMutate, noCheck, noDialog, ...rest } = options

  const mutation = useMutation<void, any, RazonSocialForm>(
    form => {
      if (!emisor) throw new Error('Emisor cargado incorrectamente')
      onBefore && onBefore(form)
      return updateEmisor(workspaceId, emisor.idRfc, form)
    },
    {
      ...rest,
      onError: async (error, form, ctx) => {
        if (error.message === 'mutation-cancelled') return
        const op = selectErrorDialog(error)
        if (op?.variant) {
          await dialog[op.variant](op)
          return
        }
        if (!noDialog) {
          const rp = await dialog.dangerAsync({
            title: 'generic.error.title',
            subtitle:
              'No se pudo actualizar la información del emisor seleccionado.',
            icon: ErrorI as TModalIcon,
            primaryButtonText: 'Reintentar',
            secondaryButtonText: 'Cancelar',
          })

          if (rp) {
            mutation.mutate(form)
            return
          }
        }

        onError && onError(error, form, ctx)
      },
    }
  )

  return mutation
}

export const useDeleteEmisor = (
  workspaceId: number,
  options: MutationOptions<void, RazonSocial, IApiError> = {}
) => {
  const { onSuccess, onError, noDialog } = options

  const dialog = useDialog()
  const showAdditionalInformationLoading = useLoadingAdditionalInformation()

  const mutation = useMutation<void, IApiError, RazonSocial>(
    async emisor => {
      const resp = await dialog.dangerAsync({
        title: '¿Estás seguro?',
        subtitle: 'Estás a punto de eliminar a este emisor.',
        icon: Help as TModalIcon,
        primaryButtonText: 'Si, eliminar',
        secondaryButtonText: 'Cancelar',
      })
      if (resp) {
        showAdditionalInformationLoading()
        return await deleteEmisor(workspaceId, emisor.idRfc)
      }
      if (!resp) throw new Error('mutation-cancelled')
    },
    {
      onSuccess: (data, emisor, ctx) => {
        dialog.close()
        onSuccess && onSuccess(data, emisor, ctx)
      },
      onError: async (error, emisor, ctx) => {
        if (error.message === 'mutation-cancelled') return

        setTimeout(async () => {
          const op = selectErrorDialog(error)
          if (op?.variant) {
            await dialog[op.variant](op)
            return
          }

          if (!noDialog) {
            const rp = await dialog.dangerAsync({
              title: 'generic.error.title',
              subtitle: 'No se pudo eliminar el emisor seleccionado.',
              icon: ErrorI as TModalIcon,
              primaryButtonText: 'Reintentar',
              secondaryButtonText: 'Cancelar',
            })

            if (rp) {
              mutation.mutate(emisor)
              return
            }
          }

          onError && onError(error, emisor, ctx)
        }, 600)
      },
    }
  )

  return mutation
}

export default useEmisoresList
